/**
 * precheckErrors — pure helpers that flatten a check-event-params response into
 * the error lines the booking form shows and gates submit on.
 *
 * @module precheckErrors
 */

const BOOKED_CONFLICT = /already booked/i;

const cleanLines = list =>
	Array.isArray(list) ? list.filter(e => typeof e === 'string' && e.trim()).map(e => e.trim()) : [];

/**
 * Every error line of a response, deduped, in this order: root `error[]`,
 * per-period `eventsPeriodsErrors`, then `pilotsBlocker`, `sicsBlocker`,
 * `instructorsBlocker`. This is the COMPLETE set: the live pre-check and the
 * submit-time check both classify from it, so they cannot disagree.
 */
export function extractPrecheckErrors(body) {
	const data = body?.data ?? body ?? {};
	const rootErrors = Array.isArray(body?.error) ? body.error.filter(Boolean) : [];
	const periodErrors = [];
	const periodsErrorsMap = data?.eventsPeriodsErrors;
	if (periodsErrorsMap && typeof periodsErrorsMap === 'object') {
		Object.values(periodsErrorsMap).forEach(entry => {
			periodErrors.push(...cleanLines(entry?.errors));
		});
	}
	return Array.from(
		new Set([
			...rootErrors,
			...periodErrors,
			...cleanLines(data?.pilotsBlocker),
			...cleanLines(data?.sicsBlocker),
			...cleanLines(data?.instructorsBlocker)
		])
	);
}

/** `data.instructorsBlocker` lines; non-empty marks the Instructor field invalid. */
export function extractInstructorsBlocker(body) {
	const data = body?.data ?? body ?? {};
	return cleanLines(data?.instructorsBlocker);
}

/**
 * Lines that may be listed for the user: `errors` minus any line the response
 * already attaches to a resource through `blockedBy` (shown next to that
 * resource). Display only — submit gating keeps the full `errors`.
 */
export function dropResourceBlockerTwins(errors, resources) {
	const shownOnResource = new Set();
	(Array.isArray(resources) ? resources : []).forEach(r => {
		cleanLines(r?.blockedBy).forEach(b => shownOnResource.add(b));
	});
	return shownOnResource.size ? errors.filter(e => !shownOnResource.has(e)) : errors;
}

/**
 * Lines that stop submit. "already booked" is not one: it turns the event into
 * a backup (waiting-list) reservation.
 */
export function blockingPrecheckErrors(errors) {
	return errors.filter(e => !BOOKED_CONFLICT.test(String(e)));
}
